/*
  LearningWidget — compact "staff learning at a glance" card for the
  main Dashboard. Shows:
    - Every user: their own overdue, in-progress, next due date,
      expiring certs, pending announcement acknowledgements.
    - Managers/owners also see team totals (overdue training, ack debt,
      lapsed competencies, expiring certs across the roster).

  All links deep-link into Learning → Training with the right tab/view.
*/
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap, AlertTriangle, Clock, Award, Megaphone,
  Users, ArrowRight, CheckCircle2, Loader2,
} from "lucide-react";

function MetricPill({ icon: Icon, label, value, tone = "zinc", testId, href }) {
  const tones = {
    zinc: "border-zinc-200 text-zinc-700 bg-white",
    amber: "border-amber-200 text-amber-900 bg-amber-50",
    red: "border-red-200 text-red-900 bg-red-50",
    indigo: "border-indigo-200 text-indigo-900 bg-indigo-50",
  };
  const inner = (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-2xs ${tones[tone]}`}
      data-testid={testId}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="font-medium">{value}</span>
      <span className="opacity-70">{label}</span>
    </div>
  );
  if (href) return <Link to={href} className="hover:brightness-95 transition">{inner}</Link>;
  return inner;
}

export default function LearningWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/training/dashboard-summary");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="p-3 rounded-lg border border-zinc-200 shadow-none flex items-center gap-2 text-xs text-zinc-500" data-testid="learning-widget-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading learning summary…
      </Card>
    );
  }
  if (err || !data) return null;

  const { mine, team, is_manager } = data;
  const myHealthy =
    mine.overdue === 0 && mine.in_progress === 0 && mine.ack_pending === 0
    && mine.qualifications_expiring_soon === 0 && mine.qualifications_expired === 0;
  const teamHealthy = team && team.assignments_overdue === 0
    && team.ack_pending_total === 0 && team.competencies_lapsed_total === 0
    && team.qualifications_expiring_soon === 0 && team.qualifications_expired === 0;

  return (
    <Card className="p-3 sm:p-4 rounded-lg border border-zinc-200 shadow-none" data-testid="learning-widget">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-1.5">
          <GraduationCap className="h-4 w-4 text-zinc-700" />
          <h3 className="text-sm font-semibold text-zinc-950">Learning</h3>
          {is_manager && (
            <Badge className="bg-indigo-100 text-indigo-800 border-indigo-200 text-2xs px-1 py-0 h-4">manager</Badge>
          )}
        </div>
        <Link to="/learning?tab=training" className="text-2xs text-indigo-700 hover:underline inline-flex items-center gap-0.5" data-testid="learning-widget-open-link">
          Open Learning <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {/* MINE row */}
      <div className="space-y-1.5">
        <div className="text-2xs uppercase tracking-wider text-zinc-400 font-medium">My training</div>
        {myHealthy ? (
          <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="learning-widget-mine-healthy">
            <CheckCircle2 className="h-3.5 w-3.5" /> You&apos;re all up to date — nothing outstanding.
          </div>
        ) : (
          <div className="flex items-center gap-1.5 flex-wrap" data-testid="learning-widget-mine-row">
            {mine.overdue > 0 && (
              <MetricPill icon={AlertTriangle} label="overdue" value={mine.overdue}
                tone="red" testId="learning-widget-mine-overdue" href="/learning?tab=training" />
            )}
            {mine.in_progress > 0 && (
              <MetricPill icon={Clock} label="in progress" value={mine.in_progress}
                tone="amber" testId="learning-widget-mine-in-progress" href="/learning?tab=training" />
            )}
            {mine.qualifications_expired > 0 && (
              <MetricPill icon={Award} label="cert expired" value={mine.qualifications_expired}
                tone="red" testId="learning-widget-mine-cert-expired" href="/learning?tab=qualifications" />
            )}
            {mine.qualifications_expiring_soon > 0 && (
              <MetricPill icon={Award} label="cert expiring" value={mine.qualifications_expiring_soon}
                tone="amber" testId="learning-widget-mine-cert-expiring" href="/learning?tab=qualifications" />
            )}
            {mine.ack_pending > 0 && (
              <MetricPill icon={Megaphone} label="to acknowledge" value={mine.ack_pending}
                tone="indigo" testId="learning-widget-mine-ack-pending" href="/learning?tab=announcements" />
            )}
          </div>
        )}
        {mine.next_due_at && mine.overdue === 0 && (
          <div className="text-2xs text-zinc-500" data-testid="learning-widget-mine-next-due">
            Next due {new Date(mine.next_due_at).toLocaleDateString()}
          </div>
        )}
      </div>

      {/* TEAM row (managers only) */}
      {is_manager && team && (
        <>
          <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1.5">
            <div className="text-2xs uppercase tracking-wider text-zinc-400 font-medium flex items-center gap-1">
              <Users className="h-2.5 w-2.5" /> Team ({team.total_staff} staff)
            </div>
            {teamHealthy ? (
              <div className="flex items-center gap-1.5 text-xs text-emerald-700" data-testid="learning-widget-team-healthy">
                <CheckCircle2 className="h-3.5 w-3.5" /> Everyone is on track. ✓
              </div>
            ) : (
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="learning-widget-team-row">
                {team.assignments_overdue > 0 && (
                  <MetricPill icon={AlertTriangle} label="overdue"
                    value={team.assignments_overdue} tone="red"
                    testId="learning-widget-team-overdue"
                    href="/learning?tab=training" />
                )}
                {team.assignments_in_progress > 0 && (
                  <MetricPill icon={Clock} label="in progress"
                    value={team.assignments_in_progress} tone="amber"
                    testId="learning-widget-team-in-progress"
                    href="/learning?tab=training" />
                )}
                {team.competencies_lapsed_total > 0 && (
                  <MetricPill icon={Award} label="comp lapsed"
                    value={team.competencies_lapsed_total} tone="red"
                    testId="learning-widget-team-comp-lapsed"
                    href="/learning?tab=training" />
                )}
                {team.qualifications_expired > 0 && (
                  <MetricPill icon={Award} label="certs expired"
                    value={team.qualifications_expired} tone="red"
                    testId="learning-widget-team-cert-expired"
                    href="/learning?tab=training" />
                )}
                {team.qualifications_expiring_soon > 0 && (
                  <MetricPill icon={Award} label="certs expiring"
                    value={team.qualifications_expiring_soon} tone="amber"
                    testId="learning-widget-team-cert-expiring"
                    href="/learning?tab=training" />
                )}
                {team.ack_pending_total > 0 && (
                  <MetricPill icon={Megaphone} label="ack debt"
                    value={team.ack_pending_total} tone="indigo"
                    testId="learning-widget-team-ack-debt"
                    href="/learning?tab=training" />
                )}
              </div>
            )}
          </div>
          {(team.assignments_overdue > 0 || team.ack_pending_total > 0) && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <Link to="/learning?tab=training">
                <Button size="sm" variant="outline" className="h-6 text-2xs px-2" data-testid="learning-widget-team-overview-btn">
                  Open team overview
                </Button>
              </Link>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
